import { Col, Row, Spinner } from "react-bootstrap";
import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import styles from "./cartItemRow.module.scss";
import { QuantityInput } from "../../common/Form/QuantityInput";
import { useFormik } from "formik";
import { CartItem } from "@/common/entities/cart/CartItem";
import { formatNumberWithDecimals } from "@/common/helpers/util.functions";
import { ProductType } from "@/common/entities/product/Product";
import { ImageWrapper } from "@/common/components/Images/ImageWrapper";
import ProductsUtils, { ProductImageSizeType } from "@/common/utils/ProductsUtils";

export interface CartItemRowData {
    item: CartItem;
    itemUpdateCallBack: (cartId: number, quantity: number) => void;
    itemDeleteCallBack: (cartId: number) => void;
}

function translateHingePosition(position: string): string {
    if (position === "left") return "stânga";
    if (position === "right") return "dreapta";
    return position;
}

const CartItemRow = React.memo(({ item, itemUpdateCallBack, itemDeleteCallBack }: CartItemRowData) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const formik = useFormik({
        initialValues: {},
        onSubmit: async () => {},
    });

    const handleUpdateQuantity = (updatedQuantity: number, cartId: number) => {
        itemUpdateCallBack(cartId, updatedQuantity);
    };

    const handleDeleteItem = async (cartId: number) => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            await itemDeleteCallBack(cartId);
        } catch (error) {
            console.error("Error deleting item:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    const calculateUnitPrice = () => {
        if ([ProductType.glass].includes(item.product.type) && item.height && item.length) {
            const areaInSquareMeters = (Number(item.height) * Number(item.length)) / 1000000;
            return formatNumberWithDecimals(
                (Number(item.specialPriceWithVat) ? Number(item.specialPriceWithVat) : Number(item.priceWithVat)) * areaInSquareMeters,
            );
        }
        return formatNumberWithDecimals(item.specialPriceWithVat ? item.specialPriceWithVat : item.priceWithVat);
    };

    const unitPrice = calculateUnitPrice();

    return (
        <Row className={styles.productRow}>
            <Col xs={3} md={2}>
                <ImageWrapper
                    width={100}
                    height={100}
                    url={ProductsUtils.getProductCoverImageFromArray(ProductImageSizeType.medium, item.product.productImages)}
                    title={item.product.name}
                />
            </Col>
            <Col xs={9} md={4}>
                <div className={styles.name}>{item.product.name}</div>
                <small>
                    {item.length && (
                        <>
                            Lungime: <b>{formatNumberWithDecimals(Number(item.length))} mm</b>
                            <br />
                        </>
                    )}
                    {item.height && (
                        <>
                            Inaltime: <b>{formatNumberWithDecimals(Number(item.height))} mm</b>
                            <br />
                        </>
                    )}
                    {item.hingePosition && (
                        <>
                            Pozitie balamale: <b>{translateHingePosition(item.hingePosition)}</b>
                            <br />
                        </>
                    )}
                    {item.mountingType && (
                        <>
                            Tip montaj: <b>{item.mountingType}</b>
                            <br />
                        </>
                    )}
                </small>
            </Col>
            <Col xs={6} md={3} className="mt-3 mt-md-0">
                <span className={styles.priceLabel}>
                    PRET UNITAR <span>(CU TVA):</span>
                </span>
                <br />
                <span className={styles.price}>{unitPrice} Lei</span>
            </Col>
            <Col xs={5} md={2} className="mt-3 mt-md-0">
                {[ProductType.basic, ProductType.pack].includes(item.product.type) ? (
                    <QuantityInput
                        formik={formik}
                        field={"quantity-" + item.id}
                        inputValue={Number(item.quantity)}
                        quantityUpdateCallBack={handleUpdateQuantity}
                        minValue={item.product.productStock?.minQtyToOrder || 1}
                    />
                ) : (
                    <div>1 x Buc</div>
                )}
            </Col>
            <Col xs={1} md={1} className={`mt-2 mt-md-0 ${styles.remove}`}>
                {isDeleting ? <Spinner animation="border" size="sm" /> : <FaTimes className={styles.deleteIcon} onClick={() => handleDeleteItem(item.id)} />}
            </Col>
        </Row>
    );
});

export default CartItemRow;
